import { useEffect, useRef } from 'react';
import { t, Trans } from '@lingui/macro';
import { FormProvider, useForm } from 'react-hook-form';
import _ from 'lodash';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import { AUTO_COMPLETE_DEFAULT, MANUAL_ERROR_TYPE, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpTextField from '../../form/NgpTextField';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import { AGENT_XMS_HOST_FIELD } from './AgentSettingsConstants';

const formId = 'frmXmsLocation';
const titleAndLabel = t`nPoint Server URL`;
const uniqueErrorText = t`This nPoint Management URL already exists`;
const endsWithErrorText = t`The nPoint Management URL must end with /nPoint`;
const addButtonText = t`Add`;
const updateButtonText = t`Update`;
const defaultFormValues = {};

export default function XmsLocationForm({
    selectedList = [],
    selectedRecord = {},
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
}) {
    const methods = useForm({ ...USE_FORM_DEFAULT_OPTIONS, defaultValues: defaultFormValues });
    const internalId = useRef(null);
    const buttonText = useRef(addButtonText);
    const { handleSubmit, formState, reset, setError } = methods;

    useEffect(() => {
        if (!_.isEmpty(selectedRecord)) {
            internalId.current = selectedRecord[INTERNAL_ID_FIELD];
            buttonText.current = selectedRecord[INTERNAL_ID_FIELD] ? updateButtonText : addButtonText;
            reset(selectedRecord);
        }
    }, [selectedRecord, reset]);

    const handleCleanup = () => {
        internalId.current = null;
        buttonText.current = addButtonText;
        setSelectedRecord(undefined);
        reset(defaultFormValues);
    };

    const handleFormSubmit = (dataToSave = {}) => {
        const mustEndWith = '/npoint';

        if (internalId) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        if (
            _.filter(selectedList, (listItem) => {
                return (
                    _.toLower(listItem[AGENT_XMS_HOST_FIELD]) === _.toLower(dataToSave[AGENT_XMS_HOST_FIELD]) &&
                    listItem[INTERNAL_ID_FIELD] !== dataToSave[INTERNAL_ID_FIELD]
                );
            }).length > 0
        ) {
            setError(AGENT_XMS_HOST_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: uniqueErrorText,
            });
            return;
        }

        if (!_.endsWith(_.toLower(dataToSave[AGENT_XMS_HOST_FIELD]), mustEndWith)) {
            setError(AGENT_XMS_HOST_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: endsWithErrorText,
            });
            return;
        }

        return Promise.resolve()
            .then(() => onConfirm(dataToSave))
            .then(handleCleanup);
    };

    return (
        <NgpTwoButtonDialog
            title={titleAndLabel}
            confirmButtonText={buttonText.current}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
            disableBackdropClick
        >
            <FormProvider {...methods}>
                <NgpForm
                    formId={formId}
                    initialized
                    loading={false}
                    showSave={false}
                    formPadding={false}
                    showRequiredFieldsTip={false}
                    submitFunction={handleFormSubmit}
                >
                    <FormSection>
                        <NgpTextField
                            fieldName={AGENT_XMS_HOST_FIELD}
                            label={titleAndLabel}
                            required
                            inputProps={{
                                size: 60,
                                maxLength: 255,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                            helpTipText={
                                <Trans>
                                    The URL to the nPoint server, typically HTTPS. An example would be
                                    https://localhost/npoint
                                </Trans>
                            }
                        />
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </NgpTwoButtonDialog>
    );
}
